import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { supabaseClient } from '@/utils/supabaseClient';
import { useAuth } from '@/utils/Auth';
import { useTranslation } from 'react-i18next';

const SESSION_DURATION = Infinity; // Unlimited session duration old 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const WARNING_BEFORE = 2 * 60 * 1000; // 2 minutes in milliseconds (not used for unlimited sessions)
const SESSION_EXPIRE_AT_KEY = 'sessionExpireAt';

const SessionTimeoutHandler = () => {
  const { session } = useAuth();
  const [initialized, setInitialized] = useState(false);
  const [sessionExpireAt, setSessionExpireAt] = useState(0);
  const [warningShown, setWarningShown] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const toastIdRef = useRef<string | number | null>(null);
  const { t } = useTranslation();

  // Format time in MM:SS
  const formatTimeLeft = (ms: number) => {
    if (ms <= 0) return '00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  // Set session expiration time
  const setSessionExpiration = () => {
    // For unlimited sessions, we don't set an expiration time
    if (SESSION_DURATION === Infinity) {
      localStorage.removeItem(SESSION_EXPIRE_AT_KEY);
      setSessionExpireAt(0);
      return 0;
    }
    const expireAt = Date.now() + SESSION_DURATION;
    localStorage.setItem(SESSION_EXPIRE_AT_KEY, expireAt.toString());
    setSessionExpireAt(expireAt);
    return expireAt;
  };

  // Initialize session expiration time
  useEffect(() => {
    if (session) {
      if (SESSION_DURATION === Infinity) {
        // For unlimited sessions, clear any existing expiration
        localStorage.removeItem(SESSION_EXPIRE_AT_KEY);
        setSessionExpireAt(0);
        setInitialized(true);
        return;
      }

      const stored = localStorage.getItem(SESSION_EXPIRE_AT_KEY);
      if (!stored) {
        // No existing session expiration, set new one
        setSessionExpiration();
      } else {
        const storedExpireAt = parseInt(stored, 10);
        const now = Date.now();

        if (storedExpireAt <= now) {
          // Session already expired, set new expiration
          setSessionExpiration();
        } else {
          // Use existing expiration time
          setSessionExpireAt(storedExpireAt);
        }
      }
      setInitialized(true);
    }
  }, [session]);

  const extendSession = async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabaseClient.auth.refreshSession();

      if (error) throw error;

      if (session) {
        setSessionExpiration();
        setWarningShown(false);

        // Dismiss the countdown toast if it exists
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }

        toast.success(t('Session.SessionExtended'), {
          description: t('Session.SessionExtendedDesc'),
        });
      }
    } catch (error) {
      console.error('Error extending session:', error);
      handleSessionExpiration();
    }
  };
  const handleSessionExpiration = () => {
    localStorage.removeItem(SESSION_EXPIRE_AT_KEY);
    sessionStorage.clear();
    setSessionExpireAt(0);
    setInitialized(false);
    setWarningShown(false);

    // Dismiss the countdown toast if it exists
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }

    supabaseClient.auth.signOut();

    toast.dismiss();

    toast.error(t('Session.SessionExpired'), {
      description: t('Session.SessionExpiredDesc'),
    });
    console.log('Session expired, redirecting to login...');

    setTimeout(() => window.location.reload(), 1500);
  };

  // Show timeout warning toast
  const showTimeoutWarning = useCallback(() => {
    if (!warningShown) {
      // Only create the toast if it doesn't exist yet
      toastIdRef.current = toast.warning(t('Session.SessionExpiringSoon'), {
        description: (
          <>
            {t('Session.YourSessionWillExpireIn')}{' '}
            <strong id="session-countdown-timer">
              {formatTimeLeft(remainingTime)}
            </strong>
          </>
        ),
        duration: Infinity, // Keep toast open indefinitely
        action: {
          label: t('Session.ExtendSession'),
          onClick: extendSession,
        },
      });
      setWarningShown(true);
    }
  }, [t, warningShown, remainingTime, extendSession]);

  // Update the timer display in the existing toast
  useEffect(() => {
    if (warningShown && remainingTime > 0) {
      // Find and update the timer element if it exists
      const timerElement = document.getElementById('session-countdown-timer');
      if (timerElement) {
        timerElement.textContent = formatTimeLeft(remainingTime);
      }
    } else if (warningShown && remainingTime <= 0) {
      // Time has run out, dismiss the toast
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    }
  }, [remainingTime, warningShown]);

  // Check session status and manage countdown
  useEffect(() => {
    if (!initialized) return;

    // Skip timeout checks for unlimited sessions
    if (SESSION_DURATION === Infinity) {
      return;
    }

    if (!sessionExpireAt) return;

    const checkSession = () => {
      const now = Date.now();
      const timeLeft = sessionExpireAt - now;

      setRemainingTime(timeLeft);

      if (timeLeft <= 0) {
        handleSessionExpiration();
      } else if (timeLeft <= WARNING_BEFORE) {
        showTimeoutWarning();
      }
    };

    const intervalId = setInterval(checkSession, 1000);
    return () => clearInterval(intervalId);
  }, [initialized, sessionExpireAt, showTimeoutWarning]);

  return null;
};

export default SessionTimeoutHandler;
